import { ReactNode, ElementType } from "react";
import { cn } from "@/lib/utils";

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type TitleSize = "xl" | "lg" | "md" | "sm" | "xs";
type TextVariant = "body" | "subtitle" | "caption" | "overline" | "tinycaption";

interface TitleProps {
  children: ReactNode;
  size?: TitleSize;
  as?: HeadingLevel;
  className?: string;
}

interface TextProps {
  children: ReactNode;
  variant?: TextVariant;
  as?: ElementType;
  className?: string;
}

/**
 * Title component for headings
 * Separates visual size from semantic HTML tag
 *
 * @param size - Visual size: "xl" (hero), "lg" (page), "md" (section), "sm" (subsection), "xs" (small)
 * @param as - Semantic HTML tag (h1-h6), defaults based on size
 *
 * @example
 * <Title size="xl">Hero Title</Title>
 * <Title size="lg" as="h2">Page Title as H2</Title>
 * <Title size="sm">Subsection Title</Title>
 */
export function Title({ children, size = "lg", as, className }: TitleProps) {
  // Default semantic tag based on size if not specified
  const defaultTag: Record<TitleSize, HeadingLevel> = {
    xl: "h1",
    lg: "h1",
    md: "h2",
    sm: "h3",
    xs: "h4",
  };

  const Component = as || defaultTag[size];

  const sizeClasses: Record<TitleSize, string> = {
    xl: "text-4xl sm:text-5xl md:text-6xl lg:text-7xl", // Hero/Landing
    lg: "text-3xl sm:text-4xl md:text-5xl", // Page Title
    md: "text-lg sm:text-xl lg:text-2xl", // Section Heading
    sm: "text-base sm:text-lg", // Subsection Heading
    xs: "text-sm sm:text-base", // Small Heading
  };

  return (
    <Component
      className={cn(
        "font-serif text-primary-900",
        sizeClasses[size],
        className
      )}
    >
      {children}
    </Component>
  );
}

/**
 * Text component for body text, subtitles, captions
 *
 * @param variant - Text style: "body", "subtitle", "caption", "overline"
 * @param as - HTML element (p, span, div), defaults to "p"
 *
 * @example
 * <Text variant="subtitle">Supporting text for a title</Text>
 * <Text variant="body">Regular paragraph text</Text>
 */
export function Text({
  children,
  variant = "body",
  as: Component = "p",
  className,
}: TextProps) {
  const variantClasses: Record<TextVariant, string> = {
    subtitle: "text-sm sm:text-base text-gray-900",
    body: "text-xs sm:text-sm text-gray-900",
    caption: "text-xs sm:text-sm text-gray-600",
    tinycaption: "text-[9px] sm:text-[10px] text-gray-600",
    overline: "text-xs uppercase tracking-wider text-gray-500 font-semibold",
  };

  return (
    <Component className={cn(variantClasses[variant], className)}>
      {children}
    </Component>
  );
}
